import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../utils/api';
import './AiChatPanel.css';

/**
 * Inbäddningsbar AI-chat-panel (intern variant av ChatWidget för OnlineSidebar).
 *
 * Identisk logik och UI som flytande ChatWidget men utan floating-shell:
 * - Renderas inuti en parent-container (sidofält / mobil-overlay)
 * - CSS-klasser prefixade med `ai-chat-*` för att inte krocka med
 *   sidofältets egna `.chat-messages` / `.chat-msg` / `.chat-input`-klasser
 * - State är lokalt — tab-byte i sidofältet återställer konversationen
 *
 * Assistant-svar renderas via `react-markdown` + `remark-gfm` så att
 * `**bold**`, listor, tabeller och kod-block visas formaterat istället
 * för som rå markdown-text. User-meddelanden renderas som plaintext för
 * att undvika att råa pipe/asterisk-tecken tolkas som markdown.
 *
 * Visas endast om backend-availability är true (samma gate som ChatWidget).
 *
 * @returns {JSX.Element|null} Panel-UI eller `null` om backend ej tillgänglig.
 */
export default function AiChatPanel() {
  const { t } = useTranslation();
  const { isLoggedIn, getAuthHeaders } = useAuth();
  const [available, setAvailable] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!isLoggedIn) {
      setAvailable(false);
      return;
    }
    fetch(`${API_URL}/api/chat/availability`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => setAvailable(!!data.available))
      .catch(() => setAvailable(false));
  }, [isLoggedIn, getAuthHeaders]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  /**
   * Skicka aktuellt input-fält till `/api/chat` och appenda svaret i loggen.
   *
   * @param {React.FormEvent} [e] Formulärets submit-event (om triggat via submit).
   */
  const handleSend = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;

    const newUserMsg = { role: 'user', content: text };
    const newMessages = [...messages, newUserMsg];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    setError(null);

    try {
      const r = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages.slice(-10),
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setMessages([
        ...newMessages,
        {
          role: 'assistant',
          content: data.reply,
          tools: data.tools_called,
          usage: data.usage,
          pending_approval: data.pending_approval,
        },
      ]);
    } catch (err2) {
      setError(String(err2.message || err2));
    } finally {
      setSending(false);
    }
  };

  /**
   * Bekräfta ett admin-tool-anrop som krävde manuell godkänning.
   *
   * @param {string} audit_id Audit-id som returnerades med pending_approval.
   * @param {number} msgIndex Index i messages-arrayen för meddelandet som ska markeras "approved".
   */
  const handleApprove = async (audit_id, msgIndex) => {
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/chat/approve`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ audit_id }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setMessages((prev) => {
        const copy = [...prev];
        copy[msgIndex] = { ...copy[msgIndex], pending_approval: null, approved: true };
        return [
          ...copy,
          {
            role: 'assistant',
            content: `${t('chat.commandRun')}\n\n${data.response || JSON.stringify(data, null, 2)}`,
            tools: [],
          },
        ];
      });
    } catch (err2) {
      setError(String(err2.message || err2));
    }
  };

  /**
   * Avbryt ett pending approval utan att köra kommandot.
   *
   * @param {number} msgIndex Index i messages-arrayen för meddelandet som ska markeras "cancelled".
   */
  const handleCancel = (msgIndex) => {
    setMessages((prev) => {
      const copy = [...prev];
      copy[msgIndex] = { ...copy[msgIndex], pending_approval: null, cancelled: true };
      return [
        ...copy,
        {
          role: 'assistant',
          content: t('chat.cancelled'),
          tools: [],
        },
      ];
    });
  };

  if (available === null || available === false) return null;

  return (
    <div className="ai-chat-panel">
      <div className="ai-chat-messages" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="ai-chat-empty">
            {t('chat.empty')}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`ai-chat-msg ai-chat-msg-${m.role}`}>
            <div className="ai-chat-msg-content">
              {m.role === 'assistant' ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
              ) : (
                m.content
              )}
            </div>
            {m.pending_approval && (
              <div className="ai-chat-approval-box">
                <div className="ai-chat-approval-header">⚠ {t('chat.confirmRequired')}</div>
                <div className="ai-chat-approval-body">
                  <div className="ai-chat-approval-label">{t('chat.proposedCommand')}</div>
                  <code className="ai-chat-approval-cmd">
                    {m.pending_approval.tool_name}({JSON.stringify(m.pending_approval.proposed_input)})
                  </code>
                </div>
                <div className="ai-chat-approval-actions">
                  <button
                    className="ai-chat-approval-btn ai-chat-approval-btn-confirm"
                    onClick={() => handleApprove(m.pending_approval.audit_id, i)}
                  >
                    ✓ {t('chat.runCommand')}
                  </button>
                  <button
                    className="ai-chat-approval-btn ai-chat-approval-btn-cancel"
                    onClick={() => handleCancel(i)}
                  >
                    ✗ {t('matchDetail.cancel')}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="ai-chat-msg ai-chat-msg-assistant ai-chat-msg-loading">
            <span className="ai-chat-typing">●●●</span>
          </div>
        )}
        {error && <div className="ai-chat-error">⚠ {error}</div>}
      </div>

      <form className="ai-chat-input-row" onSubmit={handleSend}>
        <input
          type="text"
          className="ai-chat-input"
          placeholder={t('chat.inputPlaceholder')}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={sending}
          maxLength={2000}
        />
        <button
          type="submit"
          className="ai-chat-send-btn"
          disabled={sending || !input.trim()}
        >
          ➤
        </button>
      </form>
    </div>
  );
}
